import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import { orderBy } from 'lodash'

import CampaignPage from 'Containers/Campaigns/Page'
import { currencyFormatter } from 'Utils/Formatters'

import SubmitButton from 'Components/Form/SubmitButton'
import { PageWrapper } from 'Components/Campaigns/Page/PageWrapper'
import { LayoutDashboard } from 'Components/Layout/Dashboard'
import { Table } from 'Components/Table'
import { Td } from 'Components/Table/Td'
import { Th } from 'Components/Table/Th'
import { Tr } from 'Components/Table/Tr'

import {
  getPaymentsByCampaignId,
  clearPaymentsByCampaignId,
  editPaymentsByCampaignId,
} from 'Reducers/Campaigns/actions'
import {
  getWeekNumberAndYear,
  dropTimeZoneFromDate,
  getDateOfISOWeek,
  formatToLocalDate,
} from './../../../../shared/libraries/utils/date'
import getManagePaidAmountsCampaignData from './gql'
import useGraphQLFetch from '../../../../Utils/Hooks/useGraphQLFetch'

const formatCurrencyOptions = {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
}

const ManagePaidAmounts = props => {
  const dispatch = useDispatch()
  const [items, setItems] = useState([])
  const [cities, setCities] = useState([])
  const [syncItems, setSyncItems] = useState({})
  const [cityPlans, setCityPlans] = useState([])
  const [currencySign, setCurrencySign] = useState('$')

  const payments = useSelector(state => state.campaigns.paymentsByCampaignId)

  const { campaignId } = props.match.params

  const { data, isLoaded } = useGraphQLFetch({
    fetchFn: getManagePaidAmountsCampaignData,
    defaultFetchArgs: { campaignId },
    mapResponse: data => ({
      cityPlans: data.campaign.cityPlans.nodes,
      currencySign: data.campaign.country.currencySign,
    }),
  })

  const getInitialData = async () =>
    dispatch(getPaymentsByCampaignId(campaignId))

  const clearInitialData = async () => dispatch(clearPaymentsByCampaignId())

  useEffect(() => {
    getInitialData()

    return () => {
      clearInitialData()
    }
  }, [])

  useEffect(() => {
    if (isLoaded) {
      setCityPlans(data.cityPlans)
      setCurrencySign(data.currencySign)
    }
  }, [isLoaded])

  useEffect(() => {
    if (payments) {
      const { items, cities } = normalizeItems(payments)
      setCities(cities)
      setItems(items)
    }
  }, [payments])

  const fillEmptyDays = ({ matrix, week, year, cityName, cityPlanId }) => {
    const key = `${week}-${year}`

    if (!matrix[key]) {
      matrix[key] = {
        week: week,
        year,
      }
    }

    const startDate = getDateOfISOWeek(week, year)

    if (!matrix[key][cityName]) {
      matrix[key][cityName] = {
        cityPlanId: cityPlanId,
        startDate: formatToLocalDate(startDate),
        cost: 0,
      }
    }
  }

  const getEmptyWeek = weeks => {
    const getDiff = (a, b) => Math.abs(a - b)
    const emptyWeeks = []
    weeks.forEach((item, index) => {
      if (weeks[index + 1]) {
        const diff = getDiff(item, weeks[index + 1])
        if (diff === 2) {
          emptyWeeks.push(Number(item) + 1)
        }
      }
    })
    return emptyWeeks
  }

  const normalizeItems = payments => {
    let matrix = {}
    const cities = []

    payments.forEach(item => {
      const { name: cityName } = cityPlans.find(
        ({ id }) => id === item.cityPlanId,
      ).city

      const startDate = dropTimeZoneFromDate(item.startDate)
      const { weekNumber: week, year } = getWeekNumberAndYear(
        new Date(startDate),
      )

      const key = `${week}-${year}`
      matrix[key] = Object.assign(
        {
          week: week,
          year: year,
          [cityName]: {
            ...item,
            startDate: startDate,
            cost: item.cost / 100,
          },
        },
        matrix[key],
      )
    })

    // Throw in previos, this week and next if it doesn't exist
    cityPlans.forEach(cityPlan => {
      const cityName = cityPlan.city.name
      if (cities.includes(cityName) == false) cities.push(cityName)
      const currentDate = new Date()
      let i = 2
      while (i >= 0) {
        const { weekNumber, year } = getWeekNumberAndYear(currentDate)
        fillEmptyDays({
          matrix,
          week: weekNumber,
          year,
          cityName,
          cityPlanId: cityPlan.id,
        })

        currentDate.setDate(currentDate.getDate() + 7)
        i--
      }
      const hashMapDate = Object.keys(matrix).reduce((acc, item) => {
        const [week, year] = item.split('-')
        acc[week] = year
        return acc
      }, {})

      const sortedWeek = Object.keys(hashMapDate).sort(
        (a, b) => Number(a) - Number(b),
      )

      const emptyWeeks = getEmptyWeek(sortedWeek)
      emptyWeeks.forEach(week => {
        const year = hashMapDate[week - 1]

        fillEmptyDays({ matrix, week, year, cityName, cityPlanId: cityPlan.id })
      })
    })

    //PAD-3272 Add data for past weeks. That allows adding of permit cost on past weeks.
    matrix = Object.entries(matrix).reduce((acc, [weekKey, weekData]) => {
      acc[weekKey] = { ...weekData }
      const [weekNumber, year] = weekKey.split('-')
      const startDate = formatToLocalDate(getDateOfISOWeek(weekNumber, year))
      cityPlans.forEach(cityPlan => {
        const cityName = cityPlan.city.name
        if (!acc[weekKey][cityName])
          acc[weekKey][cityName] = {
            cityPlanId: cityPlan.id,
            startDate,
            cost: 0,
          }
      })
      return acc
    }, {})

    const sortedMatrix = orderBy(
      matrix,
      ['year', o => parseInt(o.week, 10)],
      ['desc', 'desc'],
    )

    return {
      items: Object.values(sortedMatrix),
      cities,
    }
  }

  const handleSubmit = () => {
    if (Object.keys(syncItems).length > 0) {
      const body = Object.keys(syncItems).map(key => {
        const item = syncItems[key]
        return {
          ...item,
          cost: Math.floor(Number(item.amount) * 100),
        }
      })
      dispatch(editPaymentsByCampaignId(campaignId, body))
      window.location.reload()
    }
  }

  /**
   * Render table with city plans list
   *
   * @returns {*}
   */
  const renderTable = () => {
    const rowsJSX = []
    const citiesSum = []
    let sumTotal = 0

    const headJSX = cities.map((city, index) => {
      const sum = items.reduce((sum, item) => {
        if (item[city]) sum += item[city].cost
        return sum
      }, 0)

      citiesSum.push(
        <Td className="sum-td" key={`bottom-sum-${city}`}>
          {currencyFormatter(sum, formatCurrencyOptions, currencySign)}
        </Td>,
      )
      sumTotal += sum

      return <Th key={`${city}-${index}`}>{city}</Th>
    })
    citiesSum.push(
      <Td className="sum-td" key="bottom-sum-total">
        {currencyFormatter(sumTotal, formatCurrencyOptions, currencySign)}
      </Td>,
    )

    items.forEach((item, index) => {
      rowsJSX.push(renderTableRow(item, index))
    })

    rowsJSX.push(
      <Tr key="bottom-sum">
        <Td className="fixed sum-header">
          SUM <small>(permit costs)</small>
        </Td>
        {citiesSum}
      </Tr>,
    )

    return (
      <div className="table-wrapper">
        <div className="table-scroller">
          <Table className="with-fixed">
            <thead>
              <Tr>
                <Th className="fixed" id={'week'}>
                  Week
                </Th>
                {headJSX}
                <Th className="sum-header">
                  SUM <small>(permit costs)</small>
                </Th>
              </Tr>
            </thead>
            <tbody>{rowsJSX}</tbody>
          </Table>
        </div>
      </div>
    )
  }

  /**
   * Render a row of table
   *
   * @param item
   * @returns {*}
   */
  const renderTableRow = (item, rowIndex) => {
    let sum = 0
    const values = cities.map((city, index) => {
      if (item[city]) {
        sum += item[city].cost
        return (
          <Td key={`${item.week}-${item.year}-${city}`}>
            <RenderInput
              item={item}
              city={city}
              rowIndex={rowIndex}
              cellIndex={index}
            />
          </Td>
        )
      } else {
        return <Td key={`${item.week}-${item.year}-${index}`} />
      }
    })
    return (
      <Tr key={`${item.week}-${item.year}`}>
        <Td className="fixed">
          {item.week} / {item.year}
        </Td>
        {values}
        <Td className="sum-td">
          {currencyFormatter(sum, formatCurrencyOptions, currencySign)}
        </Td>
      </Tr>
    )
  }

  /**
   * Render input
   *
   * @param item
   * @returns {*}
   */

  const RenderInput = ({ item, city, rowIndex, cellIndex }) => {
    const [value, setValue] = useState(item[city].amount)
    const onChange = event => {
      if (!event.target) return
      setValue(event.target.value)
    }
    const onBlur = event => {
      const roster = item[city]

      if (event.target) {
        items[rowIndex][city] = {
          ...roster,
          amount: event.target.value,
        }
        if (event.target.value) {
          setSyncItems(prevState => ({
            ...prevState,
            ['' + roster.cityPlanId + roster.startDate]: {
              ...roster,
              amount: event.target.value,
            },
          }))
        }
      }
    }

    return (
      <div className="cell-input-wrapper">
        <input
          className="cell-input"
          tabIndex={`${rowIndex}${('0' + (cellIndex + 1)).substr(-2)}`}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
        />
        <span className="cost">
          {currencyFormatter(
            item[city].cost,
            formatCurrencyOptions,
            currencySign,
          )}
        </span>
      </div>
    )
  }

  return (
    <CampaignPage
      active={props.nav}
      campaignId={campaignId}
      match={props.match}
      history={props.history}
    >
      <PageWrapper>
        <LayoutDashboard>
          <h3 style={{ paddingTop: 30, marginTop: 0 }}>Client Permit Costs</h3>
        </LayoutDashboard>
        <form>
          {renderTable()}
          <div className="submit-button">
            <SubmitButton value="Save" onClick={handleSubmit} />
          </div>
        </form>
      </PageWrapper>
    </CampaignPage>
  )
}

ManagePaidAmounts.propTypes = {
  getPaymentsByCampaignId: PropTypes.func.isRequired,
  clearPaymentsByCampaignId: PropTypes.func.isRequired,
  editPaymentsByCampaignId: PropTypes.func.isRequired,
  payments: PropTypes.array.isRequired,
}

export default ManagePaidAmounts



// WEBPACK FOOTER //
// ./src/Containers/Campaigns/Page/ManagePaidAmounts/index.js